if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Shop Pay button lives outside product-form, so look it up
        // on the document instead of `this`.
        this.shopPayLink = document.querySelector('.main-product-shop-pay a') || document.querySelector('.main-product-shop-pay');

        if (this.shopPayLink) {
          this.shopPayLink.addEventListener('click', this.onShopPayClick.bind(this));
        }

        // If the user picks a ring size after Shop Pay was clicked
        // (and blocked by validation), automatically continue on to
        // add-to-cart + checkout instead of requiring another click.
        this.ringSizeInput = this.closest('product-info')?.querySelector('#ring-size');
        if (this.ringSizeInput) {
          this.ringSizeInput.addEventListener('change', this.onRingSizeChange.bind(this));
        }
      }

      onRingSizeChange(evt) {
        if (sessionStorage.getItem('shopPayCheckout') !== 'true') return;
        if (!evt.target.value) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Clear the ring size error state now that a value is picked.
        const ringWrapper = evt.target.closest('.ring_size_class');
        const ringSizeError = ringWrapper?.querySelector('.ring-size--error');
        ringWrapper?.classList.remove('ring-error');
        ringSizeError?.classList.add('hidden');

        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit(this.submitButton);
        } else {
          this.onSubmitHandler(new Event('submit', { cancelable: true }));
        }
      }

      onShopPayClick(evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();

        // Flag this add-to-cart as a "go straight to checkout" request.
        sessionStorage.setItem('shopPayCheckout', 'true');

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Route through the exact same submit path as the normal
        // Add to Cart button, so ring size / engraving validation etc.
        // all still run exactly as before.
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit(this.submitButton);
        } else {
          this.onSubmitHandler(new Event('submit', { cancelable: true }));
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const isShopPayCheckout = sessionStorage.getItem('shopPayCheckout') === 'true';

        if (!isShopPayCheckout) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner').classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        const productInfo = this.closest('product-info');

        // Ring Size Validation (unchanged)
        const ringSize = productInfo?.querySelector('#ring-size');

        if (ringSize && ringSize.value === '') {
          const ringWrapper = ringSize.closest('.ring_size_class');
          const ringSizeError = ringWrapper.querySelector('.ring-size--error');

          ringWrapper.classList.add('ring-error');
          ringSizeError.classList.remove('hidden');

          ringSize.focus();

          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner').classList.add('hidden');

          // Keep the shopPayCheckout flag (if set) so that once the
          // user picks a ring size, onRingSizeChange can automatically
          // continue on to checkout without another click.

          return;
        }

        const engravingBox = productInfo?.querySelector('engraving-box');

        const engravingCheckbox = engravingBox?.querySelector('[data-engraving-checkbox]');
        const engravingInput = engravingBox?.querySelector('[data-engraving-text-input]');

        const quantity = parseInt(formData.get('quantity')) || 1;

        if (engravingBox && engravingCheckbox?.checked) {
          const value = engravingInput?.value.trim();

          if (!value) {
            if (typeof engravingBox.showRequiredError === 'function') {
              engravingBox.showRequiredError();
            } else {
              engravingBox.classList.add('add-text-error');
              engravingInput?.setAttribute('aria-invalid', 'true');
              engravingInput?.focus();
            }
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner').classList.add('hidden');

            sessionStorage.removeItem('shopPayCheckout');

            return;
          } else if (typeof engravingBox.clearErrors === 'function') {
            engravingBox.clearErrors();
          } else {
            engravingBox.classList.remove('add-text-error');
            engravingInput?.setAttribute('aria-invalid', 'false');
          }
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              sessionStorage.removeItem('shopPayCheckout');

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // If this add-to-cart was triggered via Shop Pay, skip the
            // normal cart drawer/notification rendering and go straight
            // to checkout.
            if (sessionStorage.getItem('shopPayCheckout') === 'true') {
              sessionStorage.removeItem('shopPayCheckout');
              window.location.href = '/checkout';
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            sessionStorage.removeItem('shopPayCheckout');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}